// The health payload carries no format rules, so screening keeps its own: the
// EVM chains share one shape, the rest are listed with formats taken from the
// coin manifests where the app already validates them. A chain in neither list
// has no rule at all - its value goes to the service unchecked and the service
// answers `invalid_input` itself, rather than the page refusing values it does
// not know how to read (GLBA-13837).
export const EVM_ADDRESS_REGEX = '^0x[a-fA-F0-9]{40}$'
export const EVM_TX_REGEX = '^0x[a-fA-F0-9]{64}$'

const HEX_64 = '^[a-fA-F0-9]{64}$'
const BASE58 = '1-9A-HJ-NP-Za-km-z'

const CHAIN_FORMATS = {
  btc: { address: `^((1|3)[${BASE58}]{25,34}|bc1[a-zA-Z0-9]{6,})$`, tx: HEX_64 },
  ltc: { address: `^(ltc1[02-9ac-hj-np-z]{25,87}|(L|M|1|3)[${BASE58}]{25,33})$`, tx: HEX_64 },
  bsv: { address: `^([13][${BASE58}]{25,34}|(bitcoincash:)?[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{42})$`, tx: HEX_64 },
  tron: { address: `^T[${BASE58}]{33}$`, tx: HEX_64 },
  sol: { address: `^[${BASE58}]{32,44}$`, tx: `^[${BASE58}]{86,88}$` },
  // TON hashes come as hex or as (URL-safe) base64.
  ton: { address: '^([EU][A-Za-z0-9_-]{46,48}|-?\\d+:[a-fA-F0-9]{64})$', tx: '^([a-fA-F0-9]{64}|[A-Za-z0-9+/_-]{43}=?)$' },
  dot: { address: `^[${BASE58}]{46,48}$`, tx: EVM_TX_REGEX },
  tanssi: { address: `^[${BASE58}]{46,49}$`, tx: EVM_TX_REGEX },
  cardano: { address: `^(addr1[0-9a-z]+|DdzFFz[${BASE58}]+|Ae2td[${BASE58}]+)$`, tx: HEX_64 },
  xrp: { address: `^r[${BASE58}]{24,34}$`, tx: HEX_64 },
  // Short addresses are legal; a full-length one is indistinguishable from a
  // hash, and the hash wins (tx is tried first).
  aptos: { address: '^0x[a-fA-F0-9]{1,64}$', tx: EVM_TX_REGEX },
}

const EVM_CHAINS = new Set([
  'abstract', 'apechain', 'arb', 'aurora', 'avax', 'base', 'berachain', 'blast',
  'bnb', 'boba', 'celo', 'chiliz', 'core', 'cronos', 'eth', 'etherlink', 'fantom',
  'flare', 'fraxtal', 'glmr', 'gnosis', 'harmony', 'hyperevm', 'immutable-zkevm',
  'ink', 'kaia', 'kava', 'linea', 'lisk', 'manta-pacific', 'mantle', 'metis', 'mode',
  'moonriver', 'morph', 'opbnb', 'optimism', 'plume', 'polygon', 'polygon-zkevm',
  'pulsechain', 'ronin', 'rootstock', 'scroll', 'sei', 'soneium', 'sonic', 'story',
  'swellchain', 'taiko', 'telos', 'tempo', 'unichain', 'worldchain', 'worldcoin',
  'zircuit', 'zksync', 'zora',
])

const EVM_FORMAT = { address: EVM_ADDRESS_REGEX, tx: EVM_TX_REGEX }
const NO_FORMAT = { address: null, tx: null }

export const chainFormat = key =>
  CHAIN_FORMATS[key] || (EVM_CHAINS.has(key) ? EVM_FORMAT : NO_FORMAT)

// Names for chains whose health entry carries no `name` (the service names
// some of them - see `normalizeBaselineChain`), and for keys read without a
// health entry: a report link, the PDF. A key in neither is shown in capitals
// ("xlm" → "XLM", GLBA-14002): title-casing read like a word ("Xlm", "Arb",
// "Glmr" - GLBA-13838).
const CHAIN_NAMES = {
  'polygon-zkevm': 'Polygon zkEVM',
  'immutable-zkevm': 'Immutable zkEVM',
  'manta-pacific': 'Manta Pacific',
  zksync: 'zkSync',
  opbnb: 'opBNB',
  hyperevm: 'HyperEVM',
  pulsechain: 'PulseChain',
  // Renamed to `worldcoin` by the service; the old key still names history rows.
  worldchain: 'World Chain',
  worldcoin: 'World Chain',
  apechain: 'ApeChain',
  etherlink: 'Etherlink',
  swellchain: 'Swellchain',
  arb: 'Arbitrum',
  avax: 'Avalanche',
  bnb: 'BNB Chain',
  bsv: 'Bitcoin SV',
  btc: 'Bitcoin',
  dot: 'Polkadot',
  eth: 'Ethereum',
  glmr: 'Moonbeam',
  ltc: 'Litecoin',
  sol: 'Solana',
  ton: 'TON',
  tron: 'Tron',
  xrp: 'XRP',
  // Named as before GLBA-14002, when an unlisted key was title-cased.
  abstract: 'Abstract',
  aptos: 'Aptos',
  aurora: 'Aurora',
  base: 'Base',
  berachain: 'Berachain',
  blast: 'Blast',
  boba: 'Boba',
  cardano: 'Cardano',
  celo: 'Celo',
  chiliz: 'Chiliz',
  core: 'Core',
  cronos: 'Cronos',
  fantom: 'Fantom',
  flare: 'Flare',
  fraxtal: 'Fraxtal',
  gnosis: 'Gnosis',
  harmony: 'Harmony',
  ink: 'Ink',
  kaia: 'Kaia',
  kava: 'Kava',
  linea: 'Linea',
  lisk: 'Lisk',
  mantle: 'Mantle',
  metis: 'Metis',
  mode: 'Mode',
  moonriver: 'Moonriver',
  morph: 'Morph',
  optimism: 'Optimism',
  plume: 'Plume',
  polygon: 'Polygon',
  ronin: 'Ronin',
  rootstock: 'Rootstock',
  scroll: 'Scroll',
  sei: 'Sei',
  soneium: 'Soneium',
  sonic: 'Sonic',
  story: 'Story',
  taiko: 'Taiko',
  tanssi: 'Tanssi',
  telos: 'Telos',
  tempo: 'Tempo',
  unichain: 'Unichain',
  zircuit: 'Zircuit',
  zora: 'Zora',
}

export const chainDisplayName = key =>
  CHAIN_NAMES[key] ||
  String(key || '')
    .split('-')
    .filter(Boolean)
    .join(' ')
    .toUpperCase()

// One health entry in the shape the selector, the validation and the report
// request read. Older payloads carried only the declaration, hence the
// fallback.
const capabilityReady = (status, declared) =>
  status === undefined ? Boolean(declared) : status === 'ready'

const servesAnyCheck = chain =>
  capabilityReady(chain.addressStatus, chain.address && chain.status === 'ready') ||
  capabilityReady(chain.transactionStatus, chain.transaction && chain.status === 'ready')

// The artwork the service names for a chain (GLBA-14089): the chain's own
// `chainIcon`, else the environment's icon bucket. Anything but an http(s) URL
// is dropped rather than handed to an <img>.
const iconUrl = icon => {
  const url = typeof icon === 'string' ? icon.trim() : ''

  return /^https?:\/\//i.test(url) ? url : null
}

// The name the service gives a chain wins over ours: it is the one the service
// keeps current. The key stays the identity - requests, links and icons go by it.
const serviceName = name => (typeof name === 'string' ? name.trim() : '')

export const normalizeBaselineChain = chain => ({
  key: chain.key,
  blockchain: serviceName(chain.name) || chainDisplayName(chain.key),
  label: chain.symbol,
  symbol: chain.symbol,
  coin: chain.symbol,
  family: 'eth',
  icon: iconUrl(chain.icon),
  addressRegex: chainFormat(chain.key).address,
  txRegex: chainFormat(chain.key).tx,
  // What it can serve now, not what it advertises.
  supportsAddress: capabilityReady(chain.addressStatus, chain.address),
  supportsTransaction: capabilityReady(chain.transactionStatus, chain.transaction),
  stableCoins: Array.isArray(chain.stableCoins) ? chain.stableCoins : [],
  // The native asset plus every stablecoin the chain reports.
  tokens: [
    { symbol: chain.symbol, address: '', decimals: 18, native: true },
    ...(Array.isArray(chain.stableCoins) ? chain.stableCoins : []),
  ],
})

// A chain is offered when it can serve at least one kind of check. The
// top-level `status` is a roll-up - 'degraded' when only the transaction side is
// down - and filtering on it alone hid four chains whose address checks worked
// (GLBA-13491).
export const readyBaselineChains = (chains = []) =>
  chains
    .filter(chain => chain?.key && servesAnyCheck(chain))
    .map(normalizeBaselineChain)
    .sort((a, b) => a.blockchain.localeCompare(b.blockchain))

// Every chain the service knows, health notwithstanding. Screening must not
// offer a chain it cannot check, but a history filter names chains that already
// wrote rows - filtering by health would hide those rows for the outage.
export const allBaselineChains = (chains = []) =>
  chains
    .filter(chain => chain?.key)
    .map(normalizeBaselineChain)
    .sort((a, b) => a.blockchain.localeCompare(b.blockchain))

// Identity of the offered set, so polling can skip no-op commits. Availability
// belongs in it: without that, a chain coming back up never reaches the
// dropdown.
export const chainsFingerprint = (chains = []) =>
  chains
    .map(chain =>
      [
        chain.key,
        chain.symbol,
        chain.blockchain,
        chain.supportsAddress,
        chain.supportsTransaction,
        (chain.tokens || []).map(t => t.address).join(','),
        chain.icon || '',
      ].join(':'),
    )
    .join('|')
